export interface StreamVideo {
  uid: string;
  thumbnail: string;
  preview: string;
  readyToStream: boolean;
  status: { state: string; pctComplete?: string; errorReasonText?: string };
  meta: Record<string, string>;
  created: string;
  modified: string;
  duration: number;
  size: number;
  input: { width: number; height: number };
  playback: { hls: string; dash: string };
}

export interface Caption {
  language: string;
  label: string;
  generated: boolean;
  status: string;
}

export interface AudioTrack {
  uid: string;
  label: string;
  default: boolean;
  status: string;
}

export interface GlossaryTerm {
  id: string;
  term_source: string;
  language: string; // BCP-47 code, or '*' = invariant in all languages
  term_translated: string;
  category: string | null;
  notes: string | null;
  case_sensitive: number;
  created_at: number;
  updated_at: number;
}

export interface NewGlossaryTerm {
  term_source: string;
  language: string;
  term_translated: string;
  category?: string;
  notes?: string;
  case_sensitive?: boolean;
}

export type MediaJobType = "subtitle" | "dub";
export type MediaJobStatus = "queued" | "processing" | "ready" | "failed";

export interface MediaJob {
  id: string;
  video_uid: string;
  type: MediaJobType;
  source_lang: string;
  target_lang: string;
  status: MediaJobStatus;
  step: string | null;
  provider_job_id: string | null;
  r2_key: string | null;
  stream_track_id: string | null;
  included_in_player: number;
  attempts: number;
  error: string | null;
  created_at: number;
  updated_at: number;
  created_by: string;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      ...(init.body && !(init.body instanceof FormData)
        ? { "content-type": "application/json" }
        : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status}: ${text || res.statusText}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  me: () => call<{ email: string; customerDomain: string | null }>("/api/me"),

  listVideos: (search = "") =>
    call<{ videos: StreamVideo[]; customerDomain: string }>(
      `/api/videos${search ? `?search=${encodeURIComponent(search)}` : ""}`,
    ),

  getVideo: (id: string) => call<StreamVideo>(`/api/videos/${id}`),

  updateVideo: (id: string, body: { name?: string; meta?: Record<string, string> }) =>
    call<StreamVideo>(`/api/videos/${id}`, { method: "PATCH", body: JSON.stringify(body) }),

  deleteVideo: (id: string) => call<void>(`/api/videos/${id}`, { method: "DELETE" }),

  mintUploadUrl: (name: string, size: number) =>
    call<{ uploadURL: string; uid: string }>("/api/upload-url", {
      method: "POST",
      body: JSON.stringify({ name, size }),
    }),

  setThumbnailTimestamp: (id: string, timestampSeconds: number) =>
    call<unknown>(`/api/videos/${id}/thumbnail`, {
      method: "POST",
      body: JSON.stringify({ timestampSeconds }),
    }),

  listAudio: (id: string) => call<{ audio: AudioTrack[] }>(`/api/videos/${id}/audio`),

  addAudioFile: async (
    id: string,
    file: File,
    label: string,
    language?: string,
    onProgress?: (pct: number) => void,
  ) => {
    // 1. Ask the Worker for a presigned R2 upload URL.
    const { uploadUrl, key } = await call<{ uploadUrl: string; key: string }>(
      `/api/videos/${id}/audio/presign`,
      {
        method: "POST",
        body: JSON.stringify({ filename: file.name, contentType: file.type }),
      },
    );

    // 2. PUT the file straight to R2 via the presigned URL.
    // Using XHR (not fetch) so we can report upload progress. We don't set
    // content-type explicitly — the signed URL doesn't require it, and
    // letting the browser set it from the File blob avoids signature
    // mismatches.
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress((e.loaded / e.total) * 100);
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`R2 upload failed: HTTP ${xhr.status} — ${xhr.responseText.slice(0, 300)}`));
      };
      xhr.onerror = () => reject(new Error("R2 upload network error"));
      xhr.send(file);
    });

    // 3. Tell the Worker to trigger Stream's /audio/copy with the R2 URL.
    return call<AudioTrack>(`/api/videos/${id}/audio`, {
      method: "POST",
      body: JSON.stringify({ key, label, language }),
    });
  },

  deleteAudio: (id: string, audioId: string) =>
    call<void>(`/api/videos/${id}/audio?audioId=${encodeURIComponent(audioId)}`, {
      method: "DELETE",
    }),

  setAudioDefault: (id: string, audioId: string) =>
    call<AudioTrack>(`/api/videos/${id}/audio/${audioId}`, {
      method: "PATCH",
      body: JSON.stringify({ default: true }),
    }),

  listCaptions: (id: string) => call<{ captions: Caption[] }>(`/api/videos/${id}/captions`),

  uploadCaption: async (id: string, lang: string, file: File) => {
    const form = new FormData();
    form.append("file", file, file.name);
    return call<unknown>(`/api/videos/${id}/captions/${lang}`, { method: "PUT", body: form });
  },

  deleteCaption: (id: string, lang: string) =>
    call<void>(`/api/videos/${id}/captions/${lang}`, { method: "DELETE" }),

  listJobs: (id: string) => call<{ jobs: MediaJob[] }>(`/api/videos/${id}/jobs`),

  generate: (
    id: string,
    body: { source_lang: string; target_langs: string[]; consent: boolean },
  ) =>
    call<{ created: MediaJob[]; conflicts: string[] }>(
      `/api/videos/${id}/generate`,
      { method: "POST", body: JSON.stringify(body) },
    ),

  setJobIncluded: (id: string, jobId: string, included: boolean) =>
    call<MediaJob>(`/api/videos/${id}/jobs/${jobId}`, {
      method: "PATCH",
      body: JSON.stringify({ included_in_player: included }),
    }),

  deleteJob: (id: string, jobId: string) =>
    call<void>(`/api/videos/${id}/jobs/${jobId}`, { method: "DELETE" }),

  listGlossary: () => call<{ terms: GlossaryTerm[] }>("/api/glossary"),

  createGlossaryTerm: (term: NewGlossaryTerm) =>
    call<GlossaryTerm>("/api/glossary", { method: "POST", body: JSON.stringify(term) }),

  updateGlossaryTerm: (id: string, patch: Partial<NewGlossaryTerm>) =>
    call<GlossaryTerm>(`/api/glossary/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),

  deleteGlossaryTerm: (id: string) =>
    call<void>(`/api/glossary/${id}`, { method: "DELETE" }),
};

export function embedUrl(customerDomain: string, uid: string) {
  return `https://${customerDomain}/${uid}/iframe`;
}

export function embedCode(customerDomain: string, uid: string, title = "Windhund 365") {
  const src = embedUrl(customerDomain, uid);
  return `<iframe src="${src}" loading="lazy" style="border:none;position:absolute;top:0;left:0;height:100%;width:100%;" allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;" allowfullscreen="true" title="${escapeHtml(title)}"></iframe>`;
}

/**
 * URL of the Worker-served wrapper page that embeds the Stream iframe and
 * defaults to the given audio track on load. Use when you want a single
 * URL per language.
 */
/**
 * Public hostname where the embed wrapper lives. videos.windhund.com is
 * a Worker custom domain on the windhund.com zone — gives us a clean
 * URL for customers and a real "Purge Cache" button if anything ever
 * gets stuck.
 */
export const EMBED_BASE = "https://videos.windhund.com";

export function wrapperEmbedUrl(uid: string, audioLabel?: string) {
  const base = `${EMBED_BASE}/watch/${uid}`;
  return audioLabel ? `${base}?audio=${encodeURIComponent(audioLabel)}` : base;
}

export function wrapperEmbedCode(uid: string, title = "Windhund 365", audioLabel?: string) {
  const src = wrapperEmbedUrl(uid, audioLabel);
  return `<iframe src="${src}" loading="lazy" style="border:none;position:absolute;top:0;left:0;height:100%;width:100%;" allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;" allowfullscreen="true" title="${escapeHtml(title)}"></iframe>`;
}

function escapeHtml(s: string) {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}
